import { useCallback, useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextInput } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { IconButton, Text } from 'react-native-paper';
import { useThrottledCallback } from 'use-debounce';

import { AddIcon, SendIcon } from '@/assets';
import { ChatMessageTextInput } from '@/features/chats/components/ChatMessageTextInput';
import { MediaItemPlaceholderList } from '@/features/media/components/MediaItemPlaceholderList';
import { MediaMenu } from '@/features/media/components/menu/MediaMenu';
import { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import { useVerification } from '@/features/verification/providers/useVerification';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { hasItems } from '@/shared/utils/array';
import { sleep } from '@/shared/utils/async';
import { isIOS } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      height: 'auto',
      // TODO: use theme color for boxShadow
      boxShadow: '0px -4px 4px -4px #2530591F',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
      padding: 12,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    images: { marginHorizontal: -16, paddingHorizontal: 16, paddingBottom: 16 },
    message: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-end',
      gap: 8
    },
    text: { flex: 1 },
    notVerified: {
      backgroundColor: theme.colors.surfaceContainerHighest,
      height: 80,
      width: 'auto',
      justifyContent: 'center',
      alignContent: 'center'
    }
  });

type Props = {
  onSendChatMessage: (
    message: string,
    temporaryMediaItemIds: string[]
  ) => Promise<unknown>;
  autoFocus?: boolean;
};

export const ChatMessageEditor: FC<Props> = ({
  onSendChatMessage,
  autoFocus = false
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'components.chatMessageTextInput'
  });

  const { isVerified, showVerificationModal } = useVerification();

  const textInputRef = useRef<TextInput>(null);
  const [text, setText] = useState('');

  // #region media
  const {
    onPickImage,
    onRemoveImage,
    onReorderImages,
    mediaItemPlaceholders,
    temporaryMediaItemIds,
    isImageUploadPending,
    isUploadPending
  } = useMediaPlaceholders();
  // #endregion media

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const canPickMoreImages = mediaItemPlaceholders.length < 10;

  const clearEditor = useCallback(async () => {
    // Note: we clear the editor after a short delay on iOS, as on iOS autocorrect may insert a corrected word into the input automatically
    if (isIOS) await sleep(100);
    // empty fields
    setText('');
    KeyboardController.dismiss();
  }, []);

  const handleSendChatMessage = useCallback(async () => {
    const sendPromise = onSendChatMessage(text.trim(), temporaryMediaItemIds);
    clearEditor();
    textInputRef.current?.focus();
    await sendPromise;
  }, [onSendChatMessage, text, temporaryMediaItemIds, clearEditor]);

  const handleSendIconOnPress = useThrottledCallback(
    handleSendChatMessage,
    1000
  );

  return (
    <>
      {isVerified ? (
        <View style={styles.container}>
          {(hasItems(mediaItemPlaceholders) || isImageUploadPending) && (
            <MediaItemPlaceholderList
              style={styles.images}
              items={mediaItemPlaceholders}
              onDelete={onRemoveImage}
              onReorder={onReorderImages}
              uploading={isImageUploadPending}
            />
          )}
          <View style={styles.message}>
            <MediaMenu
              visible={showMenu}
              anchor={
                <IconButton
                  icon={({ color, size }) => (
                    <AddIcon fill={color} width={size} height={size} />
                  )}
                  size={24}
                  iconColor={theme.colors.primary}
                  style={{ margin: 0 }}
                  onPress={() => setShowMenu(true)}
                />
              }
              canPickImage
              disablePickImage={!canPickMoreImages}
              onPickImage={onPickImage}
              onDismiss={() => setShowMenu(false)}
            />
            <View style={styles.text}>
              <ChatMessageTextInput
                ref={textInputRef}
                value={text}
                placeholder={t('writePrivate')}
                onValueChanged={setText}
                // oxlint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
              />
            </View>
            <IconButton
              mode="contained"
              icon={({ color, size }) => (
                <SendIcon fill={color} width={size} height={size} />
              )}
              theme={{
                colors: {
                  surfaceVariant: theme.colors.error,
                  primary: theme.colors.onPrimary
                }
              }}
              size={24}
              style={{ margin: 0 }}
              disabled={isEmptyOrNull(text) || isUploadPending}
              onPress={() => {
                handleSendIconOnPress();
              }}
            />
          </View>
        </View>
      ) : (
        <Pressable
          accessibilityRole="button"
          style={styles.notVerified}
          onPress={() => showVerificationModal()}
        >
          <Text variant="bodyLarge" style={{ textAlign: 'center' }}>
            {t('notVerified')}
          </Text>
        </Pressable>
      )}
    </>
  );
};
